import { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { colorModeContext } from '../../theme';
import { useNavigate } from 'react-router-dom';
import { clearAuth, getUser, isAdmin } from '../../lib/auth';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { authPost } from '../../lib/api';

const ADMIN_REGISTRATION_URL = '/admin/registration-requests';

const Topbar = () => {
  const theme = useTheme();
  const colorMode = useContext(colorModeContext);
  const navigate = useNavigate();
  const user = getUser();
  const userIsAdmin = isAdmin();

  const [profileAnchor, setProfileAnchor] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetForm, setResetForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const handleProfileOpen = (e) => setProfileAnchor(e.currentTarget);
  const handleProfileClose = () => setProfileAnchor(null);
  const profileOpen = Boolean(profileAnchor);

  const handleSignOut = () => {
    clearAuth();
    handleProfileClose();
    navigate('/login', { replace: true });
  };

  const handleResetOpen = () => {
    handleProfileClose();
    setResetError('');
    setResetSuccess('');
    setResetForm({ current_password: '', new_password: '', confirm_password: '' });
    setResetOpen(true);
  };

  const handleResetClose = () => {
    if (resetSubmitting) return;
    setResetOpen(false);
    setResetError('');
    setResetSuccess('');
    setResetForm({ current_password: '', new_password: '', confirm_password: '' });
  };

  const updateResetForm = (field, value) => {
    setResetForm((current) => ({ ...current, [field]: value }));
  };

  const handlePasswordReset = async () => {
    setResetError('');
    setResetSuccess('');

    if (resetForm.new_password !== resetForm.confirm_password) {
      setResetError('New password and confirmation must match.');
      return;
    }

    setResetSubmitting(true);
    try {
      await authPost('/api/auth/reset-password', resetForm);
      setResetSuccess('Password reset successful.');
      setResetForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setResetError(err.message || 'Unable to reset password.');
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" p={2} gap={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {userIsAdmin && (
          <Button
            variant="contained"
            size="small"
            href={ADMIN_REGISTRATION_URL}
            sx={{ textTransform: 'none' }}
          >
            Registration Requests
          </Button>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        {user?.role && <Chip size="small" label={user.role} />}
        <IconButton onClick={colorMode.toggleColorMode} aria-label="Toggle color mode">
          {theme.palette.mode === 'dark' ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
        </IconButton>

        <IconButton onClick={handleProfileOpen} aria-label="Account menu">
          <PersonOutlineOutlinedIcon />
        </IconButton>
        <Popover
          open={profileOpen}
          anchorEl={profileAnchor}
          onClose={handleProfileClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box px={2} py={1.5} minWidth={220}>
            <Typography variant="subtitle2">{user?.full_name || user?.email || 'Signed in'}</Typography>
            {user?.email && <Typography variant="caption" color="text.secondary">{user.email}</Typography>}
          </Box>
          <MenuList>
            <MenuItem onClick={handleResetOpen}>Reset Password</MenuItem>
            <MenuItem onClick={handleSignOut}>Log Out</MenuItem>
          </MenuList>
        </Popover>
      </Box>

      <Dialog open={resetOpen} onClose={handleResetClose} fullWidth maxWidth="xs">
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {resetError && <Alert severity="error">{resetError}</Alert>}
            {resetSuccess && <Alert severity="success">{resetSuccess}</Alert>}
            <TextField
              label="Current password"
              type="password"
              value={resetForm.current_password}
              onChange={(e) => updateResetForm('current_password', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="New password"
              type="password"
              value={resetForm.new_password}
              onChange={(e) => updateResetForm('new_password', e.target.value)}
              fullWidth
              required
              helperText="Use at least 6 characters."
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={resetForm.confirm_password}
              onChange={(e) => updateResetForm('confirm_password', e.target.value)}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetClose} disabled={resetSubmitting}>Cancel</Button>
          <Button
            onClick={handlePasswordReset}
            variant="contained"
            disabled={resetSubmitting || !resetForm.current_password || !resetForm.new_password || !resetForm.confirm_password}
          >
            {resetSubmitting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Topbar;
